(() => {
  let shippingObserver;
  let offerSyncVersion = 0;
  const handledShippingSuccessEvents = new WeakSet();
  const handledShippingSuccessDetails = new WeakSet();
  const shippingFeedbackTimers = new WeakMap();
  const pickerCarts = new WeakMap();
  const rootPath = () => window.Shopify?.routes?.root || '/';
  const eligibleIds = (root) => {
    const source = root.querySelector('[data-pp-offer-eligible-variants]');
    if (!source) return new Set();
    try { return new Set(JSON.parse(source.textContent).map(String)); } catch (_) { return new Set(); }
  };
  const discountApplied = (cart, eligible) => {
    const lines = (cart.items || []).filter((item) => eligible.has(String(item.variant_id)));
    if (lines.reduce((sum, item) => sum + Number(item.quantity || 0), 0) < 3) return false;
    const namedOffer = (discount) => String(discount?.discount_application?.title || discount?.title || '').trim().toUpperCase() === 'BUY 2 GET 1 FREE';
    // A reduction by itself may be another promotion. Only the approved native
    // offer's named allocation can mark its one-per-order redemption as consumed.
    return lines.some((item) => Number(item.final_line_price) < Number(item.original_line_price)
      && [...(item.line_level_discount_allocations || []), ...(item.discount_allocations || []), ...(item.discounts || [])].some(namedOffer));
  };
  const formatPickerMoney = (root, cents) => {
    const value = Number(cents || 0);
    const amount = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value / 100);
    const amountNoDecimals = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value / 100);
    const template = root.dataset.moneyFormat || '{{amount}}';
    return template
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace(/,/g, 'X').replace(/\./g, ',').replace(/X/g, '.'))
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  };
  const updatePickerCartState = (root, cart) => {
    pickerCarts.set(root, cart);
    const eligible = eligibleIds(root);
    const applied = discountApplied(cart, eligible);
    const actual = root.querySelector('[data-pp-mix-match-actual-totals]');
    const actualDiscount = Math.max(
      Number(cart?.total_discount || 0),
      (cart?.items || []).reduce((sum, item) => sum + Math.max(0, Number(item.original_line_price || 0) - Number(item.final_line_price || 0)), 0)
    );
    if (actual) {
      actual.hidden = !(Number(cart?.item_count) > 0);
      const discount = actual.querySelector('[data-pp-mix-match-actual-discount]');
      const total = actual.querySelector('[data-pp-mix-match-cart-total]');
      if (discount) discount.textContent = formatPickerMoney(root, actualDiscount);
      if (total) total.textContent = formatPickerMoney(root, cart?.total_price || 0);
    }
    root.toggleAttribute('data-cart-offer-applied', applied);
    root.dispatchEvent(new Event('pp:offer:cart-state'));
  };
  const updateProgress = (cart) => {
    document.querySelectorAll('[data-pp-offer-progress][data-active="true"]').forEach((root) => {
      const eligible = eligibleIds(root);
      const quantity = (cart.items || []).reduce((sum, item) => eligible.has(String(item.variant_id)) ? sum + item.quantity : sum, 0);
      const step = Math.min(3, quantity);
      const excluded = (cart.items || []).filter((item) => !eligible.has(String(item.variant_id)));
      const exclusions = root.querySelector('[data-pp-offer-exclusions]');
      if (exclusions) {
        exclusions.hidden = excluded.length === 0;
        exclusions.textContent = exclusions.getAttribute(excluded.some((item) => item.has_components || item.item_components?.length) ? 'data-message-bundles' : 'data-message-items') || '';
      }
      const track = root.querySelector('[role="progressbar"]');
      const message = root.querySelector('[data-pp-offer-message]');
      root.querySelector('[data-pp-offer-count]').innerHTML = `<bdi dir="ltr">${step} / 3</bdi>`;
      root.querySelector('[data-pp-offer-progress-fill]').style.inlineSize = `${(step / 3) * 100}%`;
      track?.setAttribute('aria-valuenow', String(step));
      const applied = step === 3 && discountApplied(cart, eligible);
      if (message) message.textContent = message.getAttribute(applied ? 'data-message-3-applied' : `data-message-${step}`) || '';
      root.toggleAttribute('data-discount-applied', applied);
      root.dataset.step = String(step);
    });
  };
  const updateShippingNotice = (cart) => {
    const quantity = Number(cart?.item_count || 0);
    initShippingMotion();
    document.querySelectorAll('[data-pp-free-shipping-notice][data-active="true"]').forEach((root) => {
      const message = root.querySelector('[data-pp-free-shipping-message]');
      if (message) message.textContent = message.getAttribute(quantity > 0 ? 'data-nonempty-message' : 'data-empty-message') || '';
      root.dataset.cartQuantity = String(quantity);
    });
  };
  const canRunShippingFeedback = (root) => {
    if (document.hidden || root.hidden || !root.isConnected || !root.classList.contains('is-shipping-visible')) return false;
    if (root.closest('[aria-busy="true"]')) return false;
    const style = window.getComputedStyle(root);
    const rect = root.getBoundingClientRect();
    return style.display !== 'none'
      && style.visibility !== 'hidden'
      && rect.width > 0
      && rect.height > 0
      && rect.bottom > 0
      && rect.top < window.innerHeight;
  };
  const runShippingSuccessFeedback = (animatedRoots = new WeakSet()) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.querySelectorAll('[data-pp-free-shipping-notice][data-active="true"]').forEach((root) => {
      if (animatedRoots.has(root) || !canRunShippingFeedback(root)) return;
      animatedRoots.add(root);
      const previousTimer = shippingFeedbackTimers.get(root);
      if (previousTimer) window.clearTimeout(previousTimer);
      root.classList.remove('is-cart-arrival');
      void root.offsetWidth;
      root.classList.add('is-cart-arrival');
      shippingFeedbackTimers.set(root, window.setTimeout(() => {
        root.classList.remove('is-cart-arrival');
        shippingFeedbackTimers.delete(root);
      }, 2450));
    });
  };
  const initShippingMotion = () => {
    const notices = [...document.querySelectorAll('[data-pp-free-shipping-notice][data-active="true"]')];
    const surfaces = new WeakSet();
    notices.forEach((notice) => {
      const surface = notice.closest('[data-pp-purchase-panel], [data-pp-cart-page], [data-pp-cart-drawer-root]') || notice.parentElement;
      if (surface && surfaces.has(surface)) {
        notice.remove();
        return;
      }
      if (surface) surfaces.add(surface);
    });
    if (!('IntersectionObserver' in window)) {
      notices.filter((notice) => notice.isConnected).forEach((notice) => notice.classList.add('is-shipping-visible'));
      return;
    }
    shippingObserver ||= new IntersectionObserver((entries) => {
      entries.forEach((entry) => entry.target.classList.toggle('is-shipping-visible', entry.isIntersecting && entry.intersectionRatio > 0));
    }, { threshold: 0.1 });
    shippingObserver.disconnect();
    notices.filter((notice) => notice.isConnected).forEach((notice) => shippingObserver.observe(notice));
  };
  const updateOfferSurfaces = (cart) => {
    offerSyncVersion += 1;
    updateProgress(cart);
    updateShippingNotice(cart);
    document.querySelectorAll('[data-pp-mix-match][data-active="true"]').forEach((root) => updatePickerCartState(root, cart));
  };
  const fetchCart = async () => {
    const response = await fetch(`${rootPath()}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart could not be loaded.');
    return response.json();
  };
  const initPicker = (picker) => {
    if (picker.dataset.ppReady === 'true' || picker.dataset.active !== 'true') return;
    picker.dataset.ppReady = 'true';
    const selects = [...picker.querySelectorAll('[data-pp-mix-match-select]')];
    const slots = [...picker.querySelectorAll('[data-pp-mix-match-slot]')];
    const status = picker.querySelector('[data-pp-mix-match-status]');
    const warning = picker.querySelector('[data-pp-mix-match-warning]');
    const recheck = picker.querySelector('[data-pp-mix-match-recheck]');
    const dialog = picker.querySelector('[data-pp-mix-match-dialog]');
    const closeButton = picker.querySelector('[data-pp-mix-match-close]');
    const search = picker.querySelector('[data-pp-mix-match-search]');
    const choices = [...picker.querySelectorAll('[data-pp-mix-match-choice]')];
    const noResults = picker.querySelector('[data-pp-mix-match-no-results]');
    const total = picker.querySelector('[data-pp-mix-match-total]');
    const selectedCount = picker.querySelector('[data-pp-mix-match-selected-count]');
    const eligible = eligibleIds(picker);
    const records = slots.map(() => null);
    const property = '_pp_mix_match_slot';
    let activeSlot = null;
    let returnFocus = null;
    let pending = false;
    let uncertain = null;
    const token = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const linesFor = (cart, record) => record ? (cart?.items || []).filter((item) => item.properties?.[property] === record.token && String(item.variant_id) === record.id) : [];
    const quantityFor = (cart, record) => linesFor(cart, record).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const ensureOption = (select, choice) => {
      let option = [...select.options].find((item) => item.value === choice.dataset.variantId);
      if (!option) { option = document.createElement('option'); option.value = choice.dataset.variantId; select.append(option); }
      option.textContent = `${choice.dataset.productTitle || ''}${choice.dataset.variantTitle ? ` — ${choice.dataset.variantTitle}` : ''}`;
      ['productTitle', 'variantTitle', 'priceCents', 'price', 'comparePriceCents', 'comparePrice', 'imageSrc', 'imageAlt'].forEach((key) => { option.dataset[key] = choice.dataset[key] || ''; });
      return option;
    };
    const render = () => {
      let subtotal = 0;
      records.forEach((record, index) => {
        const slot = slots[index];
        const option = record ? ensureOption(selects[index], record.choice) : null;
        selects[index].value = record?.id || '';
        slot.querySelector('[data-pp-mix-match-empty]').hidden = Boolean(record);
        slot.querySelector('[data-pp-mix-match-selection]').hidden = !record;
        slot.querySelector('[data-pp-mix-match-slot-actions]').hidden = !record;
        slot.classList.toggle('is-selected', Boolean(record));
        if (!option) return;
        const image = slot.querySelector('[data-pp-mix-match-image]');
        image.src = option.dataset.imageSrc || '';
        image.alt = option.dataset.imageAlt || option.dataset.productTitle || '';
        image.hidden = !option.dataset.imageSrc;
        slot.querySelector('[data-pp-mix-match-title]').textContent = option.dataset.productTitle;
        const variant = slot.querySelector('[data-pp-mix-match-variant]');
        variant.textContent = option.dataset.variantTitle;
        variant.hidden = !option.dataset.variantTitle;
        slot.querySelector('[data-pp-mix-match-price]').textContent = option.dataset.price || formatPickerMoney(picker, option.dataset.priceCents);
        const compare = slot.querySelector('[data-pp-mix-match-compare]');
        compare.textContent = option.dataset.comparePrice;
        compare.hidden = !option.dataset.comparePriceCents;
        subtotal += Number(option.dataset.priceCents || 0);
      });
      total.textContent = formatPickerMoney(picker, subtotal);
      selectedCount.textContent = (selectedCount.dataset.labelTemplate || '').replace('__COUNT__', String(records.filter(Boolean).length)).replace(/(\d+\s*\/\s*\d+)/g, '\u2066$1\u2069');
      picker.querySelectorAll('[data-pp-mix-match-open], [data-pp-mix-match-remove], [data-pp-mix-match-choice]').forEach((button) => { button.disabled = pending || Boolean(uncertain); });
      recheck.disabled = pending;
      picker.dataset.pending = String(pending);
      picker.setAttribute('aria-busy', String(pending));
    };
    const reconcile = (cart, transaction = null) => {
      if (transaction) {
        const { index, old, next } = transaction;
        if (old && transaction.oldQuantity !== null && quantityFor(cart, old) < transaction.oldQuantity) transaction.oldReleased = true;
        if (next && quantityFor(cart, next) > 0) transaction.addConfirmed = true;
        records[index] = next && quantityFor(cart, next) > 0 ? next : old && !transaction.oldReleased && quantityFor(cart, old) > 0 ? old : null;
      }
      records.forEach((record, index) => { if (record && quantityFor(cart, record) === 0) records[index] = null; });
      updateOfferSurfaces(cart);
      render();
    };
    picker.addEventListener('pp:offer:cart-state', () => {
      if (!pending && !uncertain) records.forEach((record, index) => { if (record && quantityFor(pickerCarts.get(picker), record) === 0) records[index] = null; });
      render();
    });
    const showStatus = (message, error = false) => {
      status.textContent = message;
      status.className = `pp-alert ${error ? 'pp-alert--error' : 'pp-alert--success'}`;
      status.hidden = false;
    };
    const paintCart = async (cart, sections = {}) => {
      if (window.PerfumePalaceCart?.refresh) return window.PerfumePalaceCart.refresh(sections, cart);
      updateOfferSurfaces(cart);
      return cart;
    };
    const announce = (transaction) => {
      if (!transaction.addConfirmed || transaction.announced) return;
      transaction.announced = true;
      document.dispatchEvent(new CustomEvent('pp:product:added', { detail: { source: 'mix-match', button: transaction.trigger, payload: transaction.payload, items: [{ id: Number(transaction.next.id), quantity: 1 }] } }));
    };
    const finish = (transaction, cart, refreshed) => {
      if (transaction.addConfirmed) showStatus(picker.dataset.added);
      else if (transaction.oldReleased && !transaction.next) showStatus(picker.dataset.removed);
      if (cart && discountApplied(cart, eligible) && transaction.addConfirmed) status.textContent += ` ${picker.dataset.applied}`;
      announce(transaction);
    };
    const post = async (path, data, transaction) => {
      transaction.mutationUncertain = true;
      const response = await fetch(`${rootPath()}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...data, sections: ['pp-cart-drawer', 'cart-template'], sections_url: window.location.pathname }) });
      const payload = await response.json();
      if (!response.ok) { transaction.mutationUncertain = false; throw new Error(payload.description || payload.message || picker.dataset.error); }
      transaction.mutationUncertain = false;
      return payload;
    };
    const changeSlot = async (index, choice, trigger) => {
      if (pending || uncertain || index < 0 || !slots[index]) return;
      if (choice && (!eligible.has(choice.dataset.variantId) || !choices.includes(choice) || choice.disabled)) return;
      const old = records[index];
      if ((!old && !choice) || (old && choice?.dataset.variantId === old.id)) return;
      const transaction = { index, old, next: choice ? { id: choice.dataset.variantId, token: token(), choice } : null, oldQuantity: null, oldReleased: false, addConfirmed: false, announced: false, trigger, mutationUncertain: false };
      pending = true;
      warning.hidden = true;
      recheck.hidden = true;
      showStatus(picker.dataset.updating);
      render();
      let cart = null;
      let sections = {};
      let refreshed = false;
      try {
        if (old) {
          cart = await fetchCart();
          transaction.oldQuantity = quantityFor(cart, old);
          const line = linesFor(cart, old).find((item) => Number(item.quantity) > 0);
          if (line) {
            cart = await post('cart/change.js', { id: line.key, quantity: Number(line.quantity) - 1 }, transaction);
            sections = cart.sections || {};
          }
          transaction.oldReleased = true;
          records[index] = null;
          reconcile(cart, transaction);
        }
        if (transaction.next) {
          // A private line property keeps this unit separate from pre-existing
          // quantities. Never infer ownership from the product/variant ID alone.
          transaction.payload = await post('cart/add.js', { items: [{ id: Number(transaction.next.id), quantity: 1, properties: { [property]: transaction.next.token } }] }, transaction);
          const added = (transaction.payload.items || []).find((item) => String(item.variant_id || item.id) === transaction.next.id && item.properties?.[property] === transaction.next.token && Number(item.quantity) >= 1);
          if (!added) { transaction.mutationUncertain = true; throw new Error(picker.dataset.checkCart); }
          transaction.addConfirmed = true;
          records[index] = transaction.next;
          sections = transaction.payload.sections || {};
          cart = null;
          cart = await fetchCart();
        }
        reconcile(cart, transaction);
        await paintCart(cart, sections);
        refreshed = true;
      } catch (error) {
        let reconciled = false;
        try { cart = await fetchCart(); reconcile(cart, transaction); reconciled = true; }
        catch (_) { if (transaction.mutationUncertain || !cart) uncertain = transaction; }
        if (reconciled || (cart && !transaction.mutationUncertain)) {
          try { await paintCart(cart); refreshed = true; } catch (_) {}
        }
        const unconfirmed = Boolean(uncertain);
        if (!transaction.addConfirmed) showStatus(unconfirmed ? picker.dataset.checkCart : transaction.oldReleased && transaction.next ? picker.dataset.replaceFailed : error.message || picker.dataset.error, true);
        warning.textContent = unconfirmed || !refreshed ? picker.dataset.checkCart : '';
        warning.hidden = !warning.textContent;
        recheck.hidden = !warning.textContent;
        picker.querySelector('[data-pp-mix-match-actual-totals]').hidden = !cart || unconfirmed;
      } finally {
        pending = false;
        render();
        finish(transaction, cart, refreshed);
      }
    };
    recheck.addEventListener('click', async () => {
      if (pending) return;
      pending = true; render();
      try {
        const cart = await fetchCart();
        const transaction = uncertain;
        if (transaction) reconcile(cart, transaction);
        else reconcile(cart);
        uncertain = null;
        await paintCart(cart);
        warning.hidden = true; recheck.hidden = true;
        if (transaction) finish(transaction, cart, true);
        else showStatus(picker.dataset.updated);
      } catch (_) { warning.textContent = picker.dataset.checkCart; warning.hidden = false; recheck.hidden = false; }
      finally { pending = false; render(); }
    });
    const filterChoices = () => {
      const query = (search?.value || '').trim().toLocaleLowerCase();
      let visible = 0;
      choices.forEach((choice) => { const matches = !query || (choice.dataset.searchText || '').includes(query); choice.hidden = !matches; if (matches) visible += 1; });
      noResults.hidden = visible > 0;
    };
    const closePicker = () => { if (dialog?.open) dialog.close(); };
    picker.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-pp-mix-match-remove]');
      if (remove) { const slot = remove.closest('[data-pp-mix-match-slot]'); changeSlot(Number(slot.dataset.slotIndex), null, slot.querySelector('[data-pp-mix-match-open]')); return; }
      const open = event.target.closest('[data-pp-mix-match-open]');
      if (!open || pending || uncertain || !dialog) return;
      activeSlot = open.closest('[data-pp-mix-match-slot]');
      returnFocus = open;
      const current = records[Number(activeSlot.dataset.slotIndex)]?.id;
      choices.forEach((choice) => choice.setAttribute('aria-pressed', String(choice.dataset.variantId === current)));
      if (search) search.value = '';
      filterChoices(); dialog.showModal(); (search || closeButton)?.focus({ preventScroll: true });
    });
    choices.forEach((choice) => choice.addEventListener('click', () => {
      if (!activeSlot || pending || uncertain) return;
      const index = Number(activeSlot.dataset.slotIndex);
      const trigger = activeSlot.querySelector('[data-pp-mix-match-open]');
      closePicker();
      changeSlot(index, choice, trigger);
    }));
    closeButton?.addEventListener('click', closePicker);
    search?.addEventListener('input', filterChoices);
    dialog?.addEventListener('click', (event) => { if (event.target === dialog) closePicker(); });
    dialog?.addEventListener('close', () => { returnFocus?.focus({ preventScroll: true }); activeSlot = null; });
    dialog?.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusable = [...dialog.querySelectorAll('button:not([hidden]):not(:disabled), input:not([hidden]):not(:disabled)')].filter((item) => item.offsetParent !== null);
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    picker.classList.add('is-enhanced');
    render();
  };

  const sync = async () => {
    const version = offerSyncVersion;
    try {
      const cart = await fetchCart();
      if (version === offerSyncVersion) updateOfferSurfaces(cart);
    } catch (_) {}
  };
  document.querySelectorAll('[data-pp-mix-match]').forEach(initPicker);
  initShippingMotion();
  if (document.querySelector('[data-pp-offer-progress][data-active="true"], [data-pp-free-shipping-notice][data-active="true"]')) sync();
  document.addEventListener('pp:cart:updated', (event) => updateOfferSurfaces(event.detail.cart));
  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!select.matches('[data-pp-pdp-variant], [data-pp-variant-select]')) return;
    const scope = select.closest('[data-pp-product-page], [data-pp-product-card], .pp-v2-quick-view');
    [scope, scope?.ppCard].filter(Boolean).forEach((consumer) => {
      consumer.querySelectorAll('[data-pp-offer-variants]').forEach((badge) => {
        badge.hidden = !badge.dataset.ppOfferVariants.split('|').includes(String(select.value));
      });
    });
  });
  document.addEventListener('pp:offer:cart-updated', (event) => updateOfferSurfaces(event.detail.cart));
  document.addEventListener('pp:product:added', (event) => {
    const detail = event.detail;
    if (handledShippingSuccessEvents.has(event) || (detail && typeof detail === 'object' && handledShippingSuccessDetails.has(detail))) return;
    handledShippingSuccessEvents.add(event);
    if (detail && typeof detail === 'object') handledShippingSuccessDetails.add(detail);
    const animatedRoots = new WeakSet();
    requestAnimationFrame(() => requestAnimationFrame(() => runShippingSuccessFeedback(animatedRoots)));
    window.setTimeout(() => runShippingSuccessFeedback(animatedRoots), 180);
  });
  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll('[data-pp-free-shipping-notice]').forEach((root) => {
      root.classList.toggle('is-shipping-document-hidden', document.hidden);
    });
  });
})();
